import * as React from "react";
import * as ReactDom from "react-dom/client";
import axios from "axios";
import { Application, InstanceContext } from "@core-ts/Contexts/Application";
import { ContextProviderNoContexts } from "@core-ts/Contexts/Context";
import { FrontendInstance } from "./FrontendInstance";
import { ConfigurationSet } from "@core-ts/Config";
import { StorageServiceClientWeb } from "./StorageServiceClientWeb";
import { getSessionInfo } from "./Session";
import { Request } from "@core-ts/Contexts/Request";
import { VerifiedSession } from "@core-ts/Contexts/VerifiedSession";
import { StandardError } from "@core-ts/StandardError";
import { SessionMonitor } from "./SessionMonitor";

const RootLoadingScreen: React.FC = () => {
  const [showLongLoad, setShowLongLoad] = React.useState(false);

  React.useEffect(() => {
    const t = window.setTimeout(() => setShowLongLoad(true), 10000);
    return () => window.clearTimeout(t);
  }, []);

  return (
    // biome-ignore lint/a11y/useSemanticElements: <explanation>
    <div
      className="root-boot"
      role="status"
      aria-live="polite"
      aria-label="Loading application"
    >
      <style>{`
        .root-boot {
          position: fixed; inset: 0; display: grid; place-items: center;
          background: #ffffff; z-index: 2147483646; /* just below HTML boot (#boot) */
        }
        .root-boot .wrap {
          display: grid; gap: 0.75rem; justify-items: center;
          font-family: system-ui, -apple-system, Segoe UI, Roboto, Arial, sans-serif;
          text-align: center; padding: 0 16px;
        }
        .root-boot .spinner {
          width: 40px; height: 40px; border-radius: 50%;
          border: 3px solid rgba(0,0,0,0.1); border-top-color: #000000;
          animation: rootbootspin 1s linear infinite;
        }
        @keyframes rootbootspin { to { transform: rotate(360deg); } }
        @media (prefers-reduced-motion: reduce) {
          .root-boot .spinner { animation: none; }
        }

        /* Long-load UI (after 5s) */
        .root-boot .extra { margin-top: 16px; max-width: 520px; }
        .root-boot .msg { margin: 8px 0 12px; color: #111; line-height: 1.4; }
        .root-boot .contact { margin: 10px 0 0; color: #444; font-size: 0.95rem; }
        .root-boot .contact a { color: inherit; text-decoration: underline; }
        .root-boot .refresh {
          display: inline-block; margin-top: 4px; padding: 8px 14px; cursor: pointer;
          border: 1px solid #000; background: #000; color: #fff; border-radius: 8px;
          font-weight: 600;
        }
        .root-boot .refresh:focus { outline: 2px solid #000; outline-offset: 2px; }
      `}</style>

      <div className="wrap">
        <div className="spinner" aria-hidden="true" />
        {showLongLoad && (
          <div className="extra">
            <p className="msg">
              This is taking longer than usual. Please wait a moment or try refreshing the page.
            </p>
            <button
              className="refresh"
              type="button"
              aria-label="Refresh the page"
              onClick={() => window.location.reload()}
            >
              Refresh
            </button>
            <p className="contact">
              If the problem persists, contact us at:{" "}
              <a href="mailto:support@ecsenz.com">support@ecsenz.com</a>
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export class FrontendApplication extends Application<FrontendInstance> {
  private sessionMonitoringSetup = false;
  
  constructor(applicationName: string) {
    super("frontend", applicationName, new ContextProviderNoContexts());
  }

  get isFrontend(): boolean {
    return true;
  }

  protected async setupApplicationPreInstanceInit(): Promise<void> {}

  protected async setupInstanceContext(
    instanceName: string,
    config: ConfigurationSet
  ): Promise<InstanceContext<FrontendInstance>> {
    const sessionInfo = await getSessionInfo();

    return {
      instance: new FrontendInstance(instanceName),
      config,
      storage: new StorageServiceClientWeb(),
      verifiedSession: sessionInfo ? new VerifiedSession(sessionInfo) : undefined,
      request: new Request(null, "frontend"),
    };
  }

  private setupSessionMonitoring(): void {
    if (this.sessionMonitoringSetup) return;
    this.sessionMonitoringSetup = true;

    axios.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          window.dispatchEvent(new CustomEvent('session-expired'));
        }
        return Promise.reject(error);
      }
    );
  }

  protected async runInstance(): Promise<void> {
    if (this.runFrontendInstance === undefined) {
      throw new StandardError("No frontend instance defined");
    }

    this.setupSessionMonitoring();

    root.render(
      <SessionMonitor>
        {await this.runFrontendInstance()}
      </SessionMonitor>
    );
  }

  public runFrontendInstance: (() => Promise<React.ReactNode>) | undefined;
}

const container = document.getElementById("root") as HTMLElement;
const root = ReactDom.createRoot(container);

// demount 
(window as any).__hideBoot?.();

root.render(<RootLoadingScreen />);